// React and routing imports
import React, { useContext, useState, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';

// Material-UI imports
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

// Component imports
import { AppHeader } from './component/Header/AppHeader';
import { SideMenu } from './component/Sidemenu/SideMenu';
import AppHeaderv2 from './component/Menu/ProfileOptionsMenu';

// Context imports
import { RuleStore } from './context/RuleStore';
import { UserStore } from './context/UserStore';

// View imports
import { GoalsView } from './view/GoalsView/GoalsView';
import { HomeView } from './view/HomeView/HomeView';
import { HistoryView } from './view/HistoryView/HistoryView';

// Styles
import './App.css';

// Dark background overlay - black background
const DarkBackgroundOverlay = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  zIndex: -2,
  background: '#000000',
  pointerEvents: 'none',
}));

// Animated background with circling white circles
const CirclingBackgroundContainer = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  zIndex: -1, // On top of black background, behind content
  overflow: 'hidden',
  pointerEvents: 'none',
  background: 'transparent',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0%',
    left: '-20%',
    width: '140vw',
    height: '100vh',
    background: `
      radial-gradient(circle at 0% 20%, white 3px, transparent 6px),
      radial-gradient(circle at 5% 40%, rgba(255,255,255,0.8) 2px, transparent 4px),
      radial-gradient(circle at 10% 60%, rgba(255,255,255,0.9) 4px, transparent 8px),
      radial-gradient(circle at 15% 80%, rgba(255,255,255,0.7) 3px, transparent 6px),
      radial-gradient(circle at 20% 15%, white 2px, transparent 4px),
      radial-gradient(circle at 25% 35%, rgba(255,255,255,0.8) 4px, transparent 8px),
      radial-gradient(circle at 30% 55%, rgba(255,255,255,0.6) 3px, transparent 6px),
      radial-gradient(circle at 35% 75%, rgba(255,255,255,0.9) 2px, transparent 4px),
      radial-gradient(circle at 40% 25%, white 3px, transparent 6px),
      radial-gradient(circle at 45% 45%, rgba(255,255,255,0.7) 4px, transparent 8px),
      radial-gradient(circle at 50% 65%, rgba(255,255,255,0.8) 2px, transparent 4px),
      radial-gradient(circle at 55% 85%, rgba(255,255,255,0.6) 3px, transparent 6px),
      radial-gradient(circle at 60% 30%, white 4px, transparent 8px),
      radial-gradient(circle at 65% 50%, rgba(255,255,255,0.9) 2px, transparent 4px),
      radial-gradient(circle at 70% 70%, rgba(255,255,255,0.7) 3px, transparent 6px),
      radial-gradient(circle at 75% 10%, rgba(255,255,255,0.8) 4px, transparent 8px),
      radial-gradient(circle at 80% 90%, rgba(255,255,255,0.6) 2px, transparent 4px),
      radial-gradient(circle at 85% 35%, white 3px, transparent 6px),
      radial-gradient(circle at 90% 55%, rgba(255,255,255,0.9) 4px, transparent 8px),
      radial-gradient(circle at 95% 75%, rgba(255,255,255,0.7) 2px, transparent 4px)
    `,
    animation: 'flowLeftToRight 12s linear infinite',
  },
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '0%',
    left: '-20%',
    width: '140vw',
    height: '100vh',
    background: `
      radial-gradient(circle at 2% 25%, rgba(255,255,255,0.5) 2px, transparent 4px),
      radial-gradient(circle at 8% 45%, rgba(255,255,255,0.7) 3px, transparent 6px),
      radial-gradient(circle at 12% 65%, rgba(255,255,255,0.6) 4px, transparent 8px),
      radial-gradient(circle at 18% 85%, rgba(255,255,255,0.8) 2px, transparent 4px),
      radial-gradient(circle at 22% 5%, white 3px, transparent 6px),
      radial-gradient(circle at 28% 30%, rgba(255,255,255,0.5) 4px, transparent 8px),
      radial-gradient(circle at 32% 50%, rgba(255,255,255,0.9) 2px, transparent 4px),
      radial-gradient(circle at 38% 70%, rgba(255,255,255,0.7) 3px, transparent 6px),
      radial-gradient(circle at 42% 90%, rgba(255,255,255,0.6) 4px, transparent 8px),
      radial-gradient(circle at 48% 15%, white 2px, transparent 4px),
      radial-gradient(circle at 52% 40%, rgba(255,255,255,0.8) 3px, transparent 6px),
      radial-gradient(circle at 58% 60%, rgba(255,255,255,0.5) 4px, transparent 8px),
      radial-gradient(circle at 62% 80%, rgba(255,255,255,0.9) 2px, transparent 4px),
      radial-gradient(circle at 68% 20%, rgba(255,255,255,0.7) 3px, transparent 6px),
      radial-gradient(circle at 72% 45%, rgba(255,255,255,0.6) 4px, transparent 8px),
      radial-gradient(circle at 78% 65%, white 2px, transparent 4px),
      radial-gradient(circle at 82% 85%, rgba(255,255,255,0.8) 3px, transparent 6px),
      radial-gradient(circle at 88% 10%, rgba(255,255,255,0.5) 4px, transparent 8px),
      radial-gradient(circle at 92% 35%, rgba(255,255,255,0.9) 2px, transparent 4px),
      radial-gradient(circle at 98% 55%, rgba(255,255,255,0.7) 3px, transparent 6px)
    `,
    animation: 'flowLeftToRight 16s linear infinite',
  },
  
  '@keyframes flowLeftToRight': {
    '0%': {
      transform: 'translateX(-20%)',
    },
    '100%': {
      transform: 'translateX(0%)',
    }
  }
}));

// Loading screen styled component
const LoadingScreen = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.95)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
  
  '&.fade-out': {
    animation: 'fadeOut 1s ease-out forwards',
  },
  
  '@keyframes fadeOut': {
    '0%': {
      opacity: 1,
    },
    '100%': {
      opacity: 0,
      visibility: 'hidden',
    }
  }
}));

// Trust Points title with zoom animations
const TrustPointsTitle = styled('h1')(({ theme }) => ({
  fontSize: '4rem',
  fontWeight: 'bold',
  color: 'white',
  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
  margin: 0,
  animation: 'zoomInLeft 2s ease-out, zoomOutRight 2s ease-in 3s forwards',
  
  '@keyframes zoomInLeft': {
    '0%': {
      opacity: 0,
      transform: 'scale(0.1) translateX(-1000px)',
    },
    '60%': {
      opacity: 1,
      transform: 'scale(1.05) translateX(0px)',
    },
    '100%': {
      opacity: 1,
      transform: 'scale(1) translateX(0px)',
    }
  },
  
  '@keyframes zoomOutRight': {
    '0%': {
      opacity: 1,
      transform: 'scale(1) translateX(0px)',
    },
    '40%': {
      opacity: 1,
      transform: 'scale(1.05) translateX(0px)',
    },
    '100%': {
      opacity: 0,
      transform: 'scale(0.1) translateX(1000px)',
    }
  },
  
  // Responsive font sizes
  '@media (max-width: 768px)': {
    fontSize: '3rem',
  },
  '@media (max-width: 480px)': {
    fontSize: '2rem',
  }
}));

/**
 * Main App Component
 * Root component that sets up routing and provides global layout structure
 */
function App() {
  const [showLoading, setShowLoading] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // Start fade out after 4.5 seconds (after zoom animations complete)
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, 4500);

    // Hide loading screen completely after fade out animation
    const hideTimer = setTimeout(() => {
      setShowLoading(false);
    }, 5500);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  return (
    <>
      {/* Global Dark Background Overlay */}
      <DarkBackgroundOverlay />
      
      {/* Global Animated White Circles Background */}
      <CirclingBackgroundContainer />
      
      {/* Loading Screen */}
      {showLoading && (
        <LoadingScreen className={fadeOut ? 'fade-out' : ''}>
          <TrustPointsTitle>Trust Points</TrustPointsTitle>
        </LoadingScreen>
      )}
      
      {/* Main Header */}
      <Box sx={{ flexGrow: 1 }}>
        <AppHeader title="Trust Points" />
      </Box>
      
      {/* Main Content Area */}
      <div className='content'>
        <Routes>
          {/* Goals Page Route */}
          <Route
            path="/goals"
            element={
              <RuleStore.store>
                <GoalsView />
              </RuleStore.store>
            }
          />
          
          {/* History Page Route */}
          <Route
            path="/history"
            element={
              <RuleStore.store>
                <HistoryView />
              </RuleStore.store>
            }
          />
          
          {/* Home Page Route (Default) */}
          <Route
            path="/"
            element={
              <>
                <RuleStore.store>
                  <HomeView />
                </RuleStore.store>
              </>
            }
          />
        </Routes>
      </div>
      
      {/* Side Navigation Menu */}
      <SideMenu />
    </>
  );
}

export default App;
